import { redirect } from "react-router";
import type { SectionSettings, SectionType } from "@veo/types";
import { ApiError, fetchApi } from "./api";
import { seoMeta, type MetaMatches } from "./seo";

export interface CmsSeo {
  metaTitle: string | null;
  metaDescription: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  canonicalUrl: string | null;
  noIndex: boolean;
}

export interface CmsPage {
  id: string;
  title: string;
  path: string;
  template: string;
  publishedAt: string | null;
  updatedAt: string;
  sections: { id: string; sectionType: SectionType; content: unknown; settings: SectionSettings }[];
  seo: CmsSeo | null;
}

type ResolveResult =
  { type: "page"; page: CmsPage } | { type: "redirect"; toPath: string; statusCode: number };

/**
 * Resolve a path to a CMS page.
 *
 * A redirect is followed with a real HTTP redirect rather than a client-side
 * swap, so a renamed page passes its authority on to the new address instead of
 * quietly serving different content at the old one. A miss throws a 404
 * Response, which React Router renders through the root error boundary with the
 * correct status — a crawler must not see 200 on a page that says "not found".
 */
export async function loadPage(path: string, preview?: string | null): Promise<CmsPage> {
  const query = new URLSearchParams({ path });
  if (preview) query.set("preview", preview);

  let result: ResolveResult;
  try {
    result = await fetchApi<ResolveResult>(`/public/pages/resolve?${query.toString()}`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Response("Not Found", { status: 404 });
    }
    throw error;
  }

  if (result.type === "redirect") {
    throw redirect(result.toPath, result.statusCode === 302 ? 302 : 301);
  }

  return result.page;
}

/**
 * Resolve a CMS page for a route that can render without one.
 *
 * Services, Contact, Book a consultation and Insights are coded routes: they
 * own behaviour — a form, a search, a listing — that is not expressible as
 * content. But their surrounding copy is content, and copy belongs to editors.
 *
 * So each of those routes asks for its page here. Sections present, the editor
 * is driving; none, and the built-in layout renders exactly as before. That
 * makes the handover incremental rather than a rewrite: a page becomes
 * editable the moment somebody adds a section to it in the admin, and reverts
 * by removing them.
 *
 * A redirect is deliberately re-thrown. Swallowing it would leave the old
 * address serving content after an editor had moved the page — the precise
 * failure `loadPage` redirects to avoid.
 */
export async function loadOptionalPage(
  path: string,
  preview?: string | null,
): Promise<CmsPage | null> {
  try {
    const page = await loadPage(path, preview);
    return page.sections.length > 0 ? page : null;
  } catch (error) {
    if (error instanceof Response && error.status >= 300 && error.status < 400) throw error;
    return null;
  }
}

/**
 * The path a request is asking for.
 *
 * React Router fetches loader data for a client-side navigation from the same
 * path with `.data` appended — `/about` becomes `/about.data`. A splat route
 * that passed that through would ask the CMS for a page at "/about.data",
 * get nothing, and 404 the navigation while a full page load worked fine.
 *
 * Stripping it here is safe: slugs are `[a-z0-9-]` only, so no real page path
 * can end in `.data`.
 */
export function pathFromRequest(request: Request): string {
  const url = new URL(request.url);

  let pathname = url.pathname;
  if (pathname.endsWith(".data")) pathname = pathname.slice(0, -".data".length);

  // The index route's data URL is `/_root.data`, which reduces to `/_root`.
  if (pathname === "/_root") return "/";

  return pathname === "" || pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
}

/**
 * Meta descriptors for a CMS page.
 *
 * Every value falls back through page SEO → page title → nothing, so a page an
 * editor has not filled in still emits a sensible title rather than an empty
 * tag. `noIndex` is honoured because an editor ticking that box expects it to
 * mean something. The tags themselves are assembled by `seoMeta`, so a CMS page
 * and a coded route emit the same set.
 */
export function pageMeta(
  page: CmsPage | undefined,
  options: {
    fallbackTitle?: string;
    siteName?: string;
    description?: string;
    matches?: MetaMatches;
  } = {},
) {
  const siteName = options.siteName ?? "VEO Consultancy";
  if (!page) return [{ title: `${options.fallbackTitle ?? "Page"} — ${siteName}` }];

  return seoMeta({
    matches: options.matches,
    title: page.seo?.metaTitle ?? `${page.title} — ${siteName}`,
    description: page.seo?.metaDescription ?? options.description ?? "",
    path: page.path,
    canonical: page.seo?.canonicalUrl,
    image: page.seo?.ogImageUrl,
    ogTitle: page.seo?.ogTitle,
    ogDescription: page.seo?.ogDescription,
    noIndex: page.seo?.noIndex,
  });
}
